import ReactGA from 'react-ga4';

let isInitialized = false;

/**
 * Initialize Google Analytics 4
 * @param measurementId - GA4 Measurement ID (format: G-XXXXXXXXXX)
 */
export const initializeGA4 = (measurementId: string): void => {
  if (!measurementId) {
    console.warn('GA4: Measurement ID is missing. Analytics will not be initialized.');
    return;
  }

  if (isInitialized) {
    console.warn('GA4: Already initialized. Skipping re-initialization.');
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      testMode: false, // Set to true for testing
    });
    isInitialized = true;
    console.log('GA4: Initialized successfully with Measurement ID:', measurementId);
  } catch (error) {
    console.error('GA4: Failed to initialize', error);
  }
};

/**
 * Check if GA4 is initialized
 */
export const isGA4Initialized = (): boolean => {
  return isInitialized;
};

/**
 * Format a timestamp as user-readable UTC for GA4 (e.g. "2025-02-04 10:30:45 UTC").
 * Accepts ISO string or anything Date can parse. Always returns UTC.
 */
export const formatTimestampReadableUTC = (timestamp: string | number | Date): string => {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) {
    return new Date().toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
  }
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s} UTC`;
};

/**
 * Base event parameters interface
 */
export interface BaseEventParams {
  hub: string;
  referrer_rider_code: string;
  referrer_rider_type?: string;
  timestamp: string;
}

/**
 * Form submit event parameters
 */
export interface FormSubmitParams extends BaseEventParams {
  submission_status: 'success' | 'failed';
}

/**
 * Track referral impression event
 */
export const trackReferralImpression = (params: BaseEventParams): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', 'referral_impression');
    return;
  }

  try {
    const eventParams: any = {
      hub: params.hub || 'unknown',
      referrer_rider_code: params.referrer_rider_code || 'unknown',
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    
    // Only include referrer_rider_type if it's provided
    if (params.referrer_rider_type) {
      eventParams.referrer_rider_type = params.referrer_rider_type;
    }
    
    ReactGA.event('referral_impression', eventParams);
    console.log('GA4: Event sent - referral_impression', eventParams);
  } catch (error) {
    console.error('GA4: Failed to track referral_impression', error);
  }
};

/**
 * Track referral button click event
 */
export const trackReferralButtonClick = (params: BaseEventParams): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', 'referral_button_click');
    return;
  }

  try {
    const eventParams: any = {
      hub: params.hub || 'unknown',
      referrer_rider_code: params.referrer_rider_code || 'unknown',
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    
    // Only include referrer_rider_type if it's provided
    if (params.referrer_rider_type) {
      eventParams.referrer_rider_type = params.referrer_rider_type;
    }
    
    ReactGA.event('referral_button_click', eventParams);
    console.log('GA4: Event sent - referral_button_click', eventParams);
  } catch (error) {
    console.error('GA4: Failed to track referral_button_click', error);
  }
};

/**
 * Track referral tab click event
 */
export const trackReferralTabClick = (params: BaseEventParams): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', 'referral_tab_click');
    return;
  }

  try {
    const eventParams: any = {
      hub: params.hub || 'unknown',
      referrer_rider_code: params.referrer_rider_code || 'unknown',
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    
    // Only include referrer_rider_type if it's provided
    if (params.referrer_rider_type) {
      eventParams.referrer_rider_type = params.referrer_rider_type;
    }
    
    ReactGA.event('referral_tab_click', eventParams);
    console.log('GA4: Event sent - referral_tab_click', eventParams);
  } catch (error) {
    console.error('GA4: Failed to track referral_tab_click', error);
  }
};

/**
 * Track referral form submit event
 */
export const trackReferralFormSubmit = (params: FormSubmitParams): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', 'referral_form_submit');
    return;
  }

  try {
    const eventParams: any = {
      hub: params.hub || 'unknown',
      referrer_rider_code: params.referrer_rider_code || 'unknown',
      submission_status: params.submission_status || 'unknown', // Ensure it's always set
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    
    // Only include referrer_rider_type if it's provided
    if (params.referrer_rider_type) {
      eventParams.referrer_rider_type = params.referrer_rider_type;
    }
    
    ReactGA.event('referral_form_submit', eventParams);
    console.log('GA4: Event sent - referral_form_submit', eventParams);
  } catch (error) {
    console.error('GA4: Failed to track referral_form_submit', error);
  }
};

// ISS-292770: neutral rider params — the referral events above bake in
// `referrer_rider_code`, which is the wrong noun for a rider's own bonus.
export interface RiderEventParams {
  hub: string;
  rider_code: string;
  rider_type?: string;
  timestamp: string;
}

const trackRiderEvent = (
  eventName: string,
  params: RiderEventParams,
  extra: Record<string, string | number> = {},
): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', eventName);
    return;
  }

  try {
    const eventParams: any = {
      ...extra,
      hub: params.hub || 'unknown',
      rider_code: params.rider_code || 'unknown',
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    if (params.rider_type) {
      eventParams.rider_type = params.rider_type;
    }

    ReactGA.event(eventName, eventParams);
    console.log(`GA4: Event sent - ${eventName}`, eventParams);
  } catch (error) {
    console.error(`GA4: Failed to track ${eventName}`, error);
  }
};

/** ISS-292770: joining bonus page rendered with a payload (eligible or not). */
export const trackJoiningBonusImpression = (params: RiderEventParams): void =>
  trackRiderEvent('joining_bonus_impression', params);

/** ISS-292770: rider tapped a milestone chip on the joining bonus strip. */
export const trackJoiningBonusMilestoneSelect = (
  params: RiderEventParams & { milestone_id: string },
): void => {
  const { milestone_id: milestoneId, ...rider } = params;
  trackRiderEvent('joining_bonus_milestone_select', rider, { milestone_id: milestoneId });
};

/**
 * Track referral call click event
 */
export const trackReferralCallClick = (params: BaseEventParams): void => {
  if (!isInitialized) {
    console.warn('GA4: Not initialized. Event not sent:', 'referral_call_click');
    return;
  }

  try {
    const eventParams: any = {
      hub: params.hub || 'unknown',
      referrer_rider_code: params.referrer_rider_code || 'unknown',
      timestamp: formatTimestampReadableUTC(params.timestamp),
    };
    
    // Only include referrer_rider_type if it's provided
    if (params.referrer_rider_type) {
      eventParams.referrer_rider_type = params.referrer_rider_type;
    }
    
    ReactGA.event('referral_call_click', eventParams);
    console.log('GA4: Event sent - referral_call_click', eventParams);
  } catch (error) {
    console.error('GA4: Failed to track referral_call_click', error);
  }
};
